import { HttpError } from '@/errors';
import type { ITransport } from '@/types';

/**
 * Sends event batches reliably during page unloads using
 * `fetch(..., { keepalive: true })`. Like `navigator.sendBeacon`, the request
 * is allowed to outlive the document, but unlike sendBeacon it supports custom
 * headers so the API key can travel in `x-api-key` instead of the URL.
 *
 * Falls back to `navigator.sendBeacon` only if keepalive fetch is unavailable.
 * In that fallback path the API key is placed in the JSON body, never the URL.
 */
export class BeaconTransport implements ITransport {
  readonly name = 'Beacon';

  constructor(
    private readonly apiUrl: string,
    private readonly apiKey: string,
    private readonly sessionId: string,
    private readonly csrfToken?: string
  ) {}

  isReady(): boolean {
    if (!this.sessionId) return false;
    if (typeof fetch === 'function') return true;
    return typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function';
  }

  async send(events: unknown[]): Promise<void> {
    if (!this.isReady()) {
      throw new Error('[BeaconTransport] no unload-safe transport available');
    }

    const url = `${this.apiUrl}/api/sessions/${this.sessionId}/events`;

    if (typeof fetch === 'function') {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-api-key': this.apiKey,
      };
      if (this.csrfToken) {
        headers['x-csrf-token'] = this.csrfToken;
      }
      const res = await fetch(url, {
        method: 'POST',
        keepalive: true,
        headers,
        body: JSON.stringify({ events }),
      });
      if (!res.ok) {
        throw new HttpError(res.status, `[BeaconTransport] HTTP ${res.status} ${res.statusText}`);
      }
      return;
    }

    // Legacy fallback: sendBeacon cannot set custom headers, so the key goes in
    // the JSON body. The server's apiKeyAuth middleware accepts `apiKey` from
    // the request body when the `x-api-key` header is absent.
    const blob = new Blob([JSON.stringify({ apiKey: this.apiKey, events })], {
      type: 'application/json',
    });
    const queued = navigator.sendBeacon(url, blob);
    if (!queued) {
      throw new Error('[BeaconTransport] navigator.sendBeacon failed to queue the request');
    }
  }

  dispose(): void {
    // No cleanup needed for Beacon
  }
}
